/* eslint-disable @typescript-eslint/no-explicit-any */
import { api_service } from '../api/api-service';
import { api_urls } from '../api/api-urls';

export interface ApiResponse {
    statusCode?: number;
    success?: boolean;
    message?: string;
    data: {
        [key: string]: any;
    };
}

export const getProfileDetails = async (showLoader: boolean = true): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.GetProfileDetails,
            showLoader,
        });
        return response as ApiResponse;
    } catch (error: any) {
        console.error('Error refreshing token:', error);
        throw error;
    }
};

export const uploadProfilePicture = async (file: File, showLoader: boolean = true): Promise<ApiResponse> => {
    try {
        const formData = new FormData();
        formData.append('file', file);

        const response: any = await api_service.post({
            endPoint: api_urls.UploadProfilePicture,
            payLoad: formData,
            headerType: api_service.ContentHeaders.FormData,
            showLoader,
        });

        const responseData = response?.data || response;

        if (responseData.statusCode && responseData.statusCode === 200) {
            return responseData;
        } else if (responseData.statusCode) {
            return responseData;
        }
        return { statusCode: 200, data: responseData };
    } catch (error: any) {
        console.error('Error uploading profile picture:', error);
        throw error;
    }
};

export interface ChangePasswordPayload {
    oldPassword: string;
    newPassword: string;
}

export interface ChangePasswordResponse {
    statusCode: number;
    message?: string;
    data?: any;
}

export const changePassword = async (
    payload: ChangePasswordPayload,
    showLoader: boolean = true,
): Promise<ChangePasswordResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.ChangePassword,
            payLoad: payload,
            showLoader,
        });
        const responseData = response?.data || response;

        if (responseData.statusCode) {
            return responseData;
        }
        return { statusCode: 200, message: 'Password changed successfully.' };
    } catch (error: any) {
        console.error('Error changing password:', error);
        throw error;
    }
};

export interface UpdateNotificationPreferencesPayload {
    emailNotification?: boolean;
    smsNotification?: boolean;
}

export interface UpdateNotificationPreferencesResponse {
    statusCode: number;
    message?: string;
    data?: any;
}

export const updateNotificationPreferences = async (
    payload: UpdateNotificationPreferencesPayload,
    showLoader: boolean = true,
): Promise<UpdateNotificationPreferencesResponse> => {
    try {
        const response: any = await api_service.patch({
            endPoint: api_urls.UpdateNotificationPreferences,
            payLoad: payload,
            showLoader,
        });
        const responseData = response?.data || response;

        if (responseData.statusCode) {
            return responseData;
        }
        return { statusCode: 200, message: 'Notification preferences updated successfully.' };
    } catch (error: any) {
        console.error('Error updating notification preferences:', error);
        throw error;
    }
};

export interface AddPhoneNumberPayload {
    phoneNumber?: string;
    phoneExtension?: string;
    isPrimary?: number;
}

export interface AddPhoneNumberResponse {
    statusCode?: number;
    message?: string;
    data?: any;
}

// `token` overrides the session token for the logged-out update-phone recovery flow.
export const addPhoneNumber = async (
    payload: AddPhoneNumberPayload,
    showLoader: boolean = true,
    token?: string,
): Promise<AddPhoneNumberResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.AddPhoneNumber,
            payLoad: payload,
            showLoader,
            ...(token && { customHeaders: { Authorization: `Bearer ${token}` } }),
        });
        const responseData = response?.data || response;

        if (responseData.statusCode) {
            return responseData;
        }
        return { statusCode: 200, message: 'Phone Number added successfully. Passcode sent.' };
    } catch (error: any) {
        console.error('Error adding phone number:', error);
        throw error;
    }
};

export interface VerifyPhoneOtpPayload {
    phoneNumber: string;
    otp: string | number;
    phoneExtension?: string;
    isPrimary?: number;
}

export interface VerifyPhoneOtpResponse {
    statusCode: number;
    message?: string;
    data?: any;
}

export const verifyPhoneOtp = async (
    payload: VerifyPhoneOtpPayload,
    showLoader: boolean = true,
    token?: string,
): Promise<VerifyPhoneOtpResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.VerifyPhoneOtp,
            payLoad: payload,
            showLoader,
            ...(token && { customHeaders: { Authorization: `Bearer ${token}` } }),
        });
        const responseData = response?.data || response;

        if (responseData.statusCode) {
            return responseData;
        }
        return { statusCode: 200, message: 'Phone Number verified successfully.' };
    } catch (error: any) {
        console.error('Error verifying phone Passcode:', error);
        throw error;
    }
};

export interface ResendPhoneOtpPayload {
    phoneNumber: string;
    phoneExtension?: string;
    isPrimary?: number;
}

export interface ResendPhoneOtpResponse {
    statusCode: number;
    message?: string;
    data?: any;
}

export const resendPhoneOtp = async (
    payload: ResendPhoneOtpPayload,
    showLoader: boolean = true,
    token?: string,
): Promise<ResendPhoneOtpResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.ResendphoneOtp,
            payLoad: payload,
            showLoader,
            ...(token && { customHeaders: { Authorization: `Bearer ${token}` } }),
        });
        const responseData = response?.data || response;

        if (responseData.statusCode) {
            return responseData;
        }
        return { statusCode: 200, message: 'Passcode sent successfully.' };
    } catch (error: any) {
        console.error('Error resending phone Passcode:', error);
        throw error;
    }
};

export interface UpdateUserDetailsPayload {
    firstName?: string;
    lastName?: string;
}

export interface UpdateUserDetailsResponse {
    statusCode: number;
    message?: string;
    data?: any;
}

export const updateUserDetails = async (
    payload: UpdateUserDetailsPayload,
    showLoader: boolean = true,
): Promise<UpdateUserDetailsResponse> => {
    try {
        const response: any = await api_service.patch({
            endPoint: api_urls.UpdateUserDetails,
            payLoad: payload,
            showLoader,
        });
        const responseData = response?.data || response;

        if (responseData.statusCode) {
            return responseData;
        }
        return { statusCode: 200, message: 'Your details have been updated successfully.' };
    } catch (error: any) {
        console.error('Error updating user details:', error);
        throw error;
    }
};
